import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { FaExclamationTriangle } from "react-icons/fa";
import { useSelector } from "react-redux";

const HrmHubSystem = () => {
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const [iframeHeight, setIframeHeight] = useState(0);

  const { currentUser } = useSelector(state => state.auth);
  const email = currentUser?.email;
  const hrmAppUrl = process.env.REACT_APP_HRM_HUB_APP_URL;

  const hrmOrigin = useMemo(() => {
    if (!hrmAppUrl) return null;
    try {
      return new URL(hrmAppUrl).origin;
    } catch (_) {
      return null;
    }
  }, [hrmAppUrl]);

  // Đọc path 1 LẦN khi component mount. Các lần SYNC_PATH sau đó chỉ ghi vào
  // localStorage để lần reopen sau restore đúng vị trí — KHÔNG được đụng vào
  // iframe.src của lần mount hiện tại, nếu không iframe sẽ reload toàn bộ
  // HR Hub mỗi khi user đổi route bên trong iframe.
  const [initialPath] = useState(() => {
    try {
      return localStorage.getItem("hrm_hub_app_path") || "/";
    } catch (_) {
      return "/";
    }
  });

  const configQuery =
    "useHeader=true&OpendNav=false&showLogo=false&showDropdownProfile=false&Language=en";

  const fullIframeUrl = useMemo(() => {
    if (!email || !hrmAppUrl) return "";
    const separator = initialPath.includes("?") ? "&" : "?";
    return `${hrmAppUrl}${initialPath}${separator}${configQuery}`;
  }, [email, hrmAppUrl, initialPath]);

  useEffect(() => {
    const calculateHeight = () => {
      const navbar =
        document.querySelector(".ptl-navbar-top") ||
        document.querySelector("nav.navbar") ||
        document.querySelector('[class*="navbar-top"]');
      const footer =
        document.querySelector("footer") ||
        document.querySelector('[class*="footer"]');

      const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 0;
      const footerHeight = footer ? footer.getBoundingClientRect().height : 0;

      const available = window.innerHeight - navbarHeight - footerHeight - 20;
      setIframeHeight(available);
    };

    calculateHeight();
    window.addEventListener("resize", calculateHeight);
    return () => window.removeEventListener("resize", calculateHeight);
  }, []);

  const sendAuthEmail = useCallback(() => {
    if (!iframeRef.current?.contentWindow || !email || !hrmOrigin) return;
    iframeRef.current.contentWindow.postMessage(
      { type: "AUTH_EMAIL", email },
      hrmOrigin
    );
  }, [email, hrmOrigin]);

  useEffect(() => {
    if (!hrmOrigin) return;
    const handleMessage = event => {
      if (event.origin !== hrmOrigin) return;
      const { type, path } = event.data || {};

      if (type === "AUTH_REQUEST") {
        sendAuthEmail();
        return;
      }

      if (type === "SYNC_PATH" && path) {
        localStorage.setItem("hrm_hub_app_path", path);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [hrmOrigin, sendAuthEmail]);

  const handleIframeLoad = () => {
    sendAuthEmail();
  };

  if (!email) {
    return (
      <div
        style={{ height: iframeHeight || "80vh" }}
        className="tw-flex tw-items-center tw-justify-center tw-bg-slate-50"
      >
        <div className="tw-text-center tw-p-5">
          <h4 className="tw-text-slate-500 tw-mb-3">Authentication Required</h4>
          <p className="tw-text-slate-500">
            Please login again to access the HRM Hub system.
          </p>
        </div>
      </div>
    );
  }

  if (!fullIframeUrl) {
    return (
      <div
        style={{ height: iframeHeight || "80vh" }}
        className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center tw-p-4"
      >
        <div className="tw-text-red-600 tw-mb-3">
          <FaExclamationTriangle size={48} />
        </div>
        <h4 className="tw-font-bold">HRM Hub link not found</h4>
        <p className="tw-text-slate-500">
          Please check the link again or contact the administrator.
        </p>
        <button
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-sm tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 tw-text-sm tw-cursor-pointer tw-border-0 tw-mt-2"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="tw-p-1 tw-bg-white tw-rounded-md"
      style={{ width: "100%", height: iframeHeight || "80vh" }}
    >
      <iframe
        ref={iframeRef}
        src={fullIframeUrl}
        title="HRM Hub App"
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          display: "block"
        }}
        allowFullScreen
        onLoad={handleIframeLoad}
      />
    </div>
  );
};

export default HrmHubSystem;
